import { Controller } from 'stimulus';

function popover() {
  let pop = document.getElementById('ct-pop');
  if (!pop) {
    pop = document.createElement('div');
    pop.id = 'ct-pop';
    pop.className = 'ct-pop hidden';
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-label', 'Samples');
    pop.addEventListener('mouseenter', () => { if (pop.owner) clearTimeout(pop.owner.hideTimer); });
    pop.addEventListener('mouseleave', () => { if (pop.owner) pop.owner.leave(); });
    document.body.appendChild(pop);
  }
  return pop;
}

// One task's sample chips on the Create Job tasks table (jobs/tasks/_sample_chips):
// "Show N more", and the names popover on a count chip ("All 40 samples").
// Hover or focus opens the popover, a click pins it open (so it works on a
// tablet), and Esc or a click elsewhere closes it. The whole page shares one
// popover element, positioned fixed so the table never clips it.
export default class extends Controller {
  connect() {
    this.onDown = (event) => {
      const pop = popover();
      if (pop.contains(event.target) || (this.anchor && this.anchor.contains(event.target))) return;
      this.close();
    };
    this.onKey = (event) => { if (event.key === 'Escape') this.close(); };
    this.onMove = () => this.place();
  }

  disconnect() {
    this.close();
  }

  toggleMore(event) {
    const button = event.currentTarget;
    const open = this.element.classList.toggle('is-open');
    button.textContent = open ? 'Show Less' : button.dataset.moreLabel;
  }

  hover(event) {
    const anchor = event.currentTarget;
    clearTimeout(this.hideTimer);
    if (this.anchor === anchor) return;
    clearTimeout(this.showTimer);
    this.showTimer = setTimeout(() => this.open(anchor, false), 120);
  }

  // Also fires on blur, so a click inside the popover (its text, its
  // scrollbar) must not close it: wait until the pointer is outside it.
  leave() {
    clearTimeout(this.showTimer);
    if (this.pinned) return;
    this.hideTimer = setTimeout(() => { if (!popover().matches(':hover')) this.close(); }, 180);
  }

  pin(event) {
    event.preventDefault();
    const anchor = event.currentTarget;
    if (this.anchor === anchor && this.pinned) {
      this.close();
    } else {
      this.open(anchor, true);
    }
  }

  open(anchor, pin) {
    const pop = popover();
    if (pop.owner && pop.owner !== this) pop.owner.close();
    clearTimeout(this.showTimer);
    clearTimeout(this.hideTimer);

    const keep = this.pinned && this.anchor === anchor;
    if (this.anchor && this.anchor !== anchor) this.anchor.setAttribute('aria-expanded', 'false');
    this.anchor = anchor;
    this.pinned = pin || keep;
    pop.owner = this;

    pop.innerHTML = '';
    pop.appendChild(anchor.querySelector('template').content.cloneNode(true));
    pop.classList.remove('hidden');
    anchor.setAttribute('aria-expanded', 'true');
    this.place();

    document.addEventListener('mousedown', this.onDown);
    document.addEventListener('keydown', this.onKey);
    window.addEventListener('scroll', this.onMove, true);
    window.addEventListener('resize', this.onMove);
  }

  close() {
    clearTimeout(this.showTimer);
    clearTimeout(this.hideTimer);
    const pop = document.getElementById('ct-pop');
    if (pop && pop.owner === this) {
      pop.classList.add('hidden');
      pop.owner = null;
    }
    if (this.anchor) this.anchor.setAttribute('aria-expanded', 'false');
    this.anchor = null;
    this.pinned = false;

    document.removeEventListener('mousedown', this.onDown);
    document.removeEventListener('keydown', this.onKey);
    window.removeEventListener('scroll', this.onMove, true);
    window.removeEventListener('resize', this.onMove);
  }

  // Below the chip, or above it when there's no room; kept inside the window.
  place() {
    const pop = popover();
    if (!this.anchor || pop.owner !== this) return;
    const rect = this.anchor.getBoundingClientRect();
    const height = pop.offsetHeight;
    const width = pop.offsetWidth;
    let top = rect.bottom + 6;
    if (top + height > window.innerHeight - 8 && rect.top - height - 6 > 8) top = rect.top - height - 6;
    pop.style.top = `${top}px`;
    pop.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`;
  }
}
